import styled from 'styled-components';
import {
  Solfege,
  solfegePronunciations,
  SolfegeSyllable,
  solfegeSyllables,
} from '../../../../../common/src/interval_training.ts';
import PlayIcon from '../../../assets/learn-play.svg?react';
import { PrimaryButton } from '../learn_buttons.tsx';
import { ScreenBody, ScreenTitle, shortScreen, Slack } from '../learn_screen_parts.tsx';

// Meet the syllables (issue #3720), from the "Meet the syllables (first run only)" artboard: the one
// screen a singer sees before their first solfège session, and again from "How to say the
// syllables". The seven syllables stand as a ladder, ti at the top and do at the bottom, each rung a
// step further right than the one below it, so height on the page is height in pitch. Each rung is
// one button - the syllable, how to say it and a play mark - and tapping it asks the page for that
// syllable's note; do is filled in the accent colour and called the home note. The syllables are
// the only labels: no key name and no letter names, because we teach singing, not theory.
//
// Presentational, like the other start screens: the page plays the note and remembers that the
// singer has been here. A short screen (iPhone SE) tightens the ladder rather than scrolling, so
// "Got it, let's start" stays on the screen.

export const meetSyllablesScreenId = 'meet-syllables';
export const meetSyllablesRungTestId = 'meet-syllables-rung';
export const meetSyllablesDoneButtonId = 'meet-syllables-done';

const homeSyllable: Solfege = solfegeSyllables[0];

export const meetSyllablesTitle = 'Meet the syllables';
const introLead = 'Singers name the notes of a scale with these seven words. Start at ';
const introTail = ' and they climb in this order. Tap one to hear it.';
export const meetSyllablesIntro = `${introLead}${homeSyllable}${introTail}`;
export const homeNoteText = 'the home note';
export const meetSyllablesNote =
  'do can be any note; the app picks one that fits your range. It is the home note, the bedrock every song rests on, and the other six sit above it in the same order every time.';
export const meetSyllablesDoneText = "Got it, let's start";
export const meetSyllablesFootnote = 'Shown once. Find it again under "How to say the syllables."';

// The accessible name of a rung, which is what a screen reader says and a test finds it by
export function hearSyllableLabel(syllable: SolfegeSyllable): string {
  return `Hear ${syllable}`;
}

// How much further right each rung up sits than the one below it, from the artboard
const rungStepPx = 14;

// The rungs top to bottom: ti first, do last
const rungDegrees = solfegeSyllables.map((_syllable, degree) => degree).reverse();

export const MeetSyllablesScreen = ({
  onPlay,
  onDone,
}: {
  onPlay: (degree: number) => void; // the singer tapped a syllable; 0 is do, 6 is ti
  onDone: () => void; // "Got it, let's start"
}) => (
  <ScreenBody id={meetSyllablesScreenId}>
    <ScreenTitle>{meetSyllablesTitle}</ScreenTitle>
    <Intro>
      {introLead}
      <HomeWord>{homeSyllable}</HomeWord>
      {introTail}
    </Intro>
    <Ladder>
      {rungDegrees.map((degree) => {
        const syllable = solfegeSyllables[degree];
        const home = syllable === homeSyllable;
        return (
          <Rung
            key={syllable}
            type="button"
            data-testid={meetSyllablesRungTestId}
            data-degree={degree}
            data-home={home ? 'true' : 'false'}
            aria-label={hearSyllableLabel(syllable)}
            $home={home}
            style={{ marginLeft: degree * rungStepPx }}
            onClick={() => onPlay(degree)}
          >
            <Syllable>{syllable}</Syllable>
            <Pronunciation $home={home}>
              {solfegePronunciations[syllable]}
              {home && <HomeNote>{homeNoteText}</HomeNote>}
            </Pronunciation>
            <PlayMark>
              <PlayIcon aria-hidden="true" />
            </PlayMark>
          </Rung>
        );
      })}
    </Ladder>
    <Note>{meetSyllablesNote}</Note>
    <Slack />
    <PrimaryButton id={meetSyllablesDoneButtonId} data-testid={meetSyllablesDoneButtonId} onClick={onDone}>
      {meetSyllablesDoneText}
    </PrimaryButton>
    <Footnote>{meetSyllablesFootnote}</Footnote>
  </ScreenBody>
);

const Intro = styled.div`
  margin-top: 4px;
  font-size: 14px;
  line-height: 21px;
  color: var(--color-text-darker);

  ${shortScreen} {
    font-size: 13px;
    line-height: 18px;
  }
`;

const HomeWord = styled.span`
  font-family: Jost-SemiBold, Arial, sans-serif;
  color: var(--color-tab-selected);
`;

const Ladder = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 16px;

  ${shortScreen} {
    gap: 3px;
    margin-top: 10px;
  }
`;

// The rung's colours: grey with accent type, or, for do, filled with the accent and lettered in the
// page background (white on deep purple in light mode, dark on lavender in dark mode)
const Rung = styled.button<{ $home: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  box-sizing: border-box;
  min-height: 56px;
  padding: 6px 10px 6px 14px;
  border: none;
  border-radius: 12px;
  background-color: ${({ $home }) => ($home ? 'var(--color-tab-selected)' : 'var(--color-background-search)')};
  color: ${({ $home }) => ($home ? 'var(--color-background)' : 'var(--color-tab-selected)')};
  font: inherit;
  text-align: left;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    filter: brightness(0.92);
  }

  ${shortScreen} {
    min-height: 44px;
    padding: 2px 8px 2px 14px;
  }
`;

const Syllable = styled.span`
  flex: none;
  width: 54px;
  font-family: Jost-SemiBold, Arial, sans-serif;
  font-size: 26px;
  line-height: 32px;

  ${shortScreen} {
    font-size: 23px;
    line-height: 28px;
  }
`;

// Grey on a grey rung; on do it takes the rung's own lettering
const Pronunciation = styled.span<{ $home: boolean }>`
  flex: auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  font-size: 14px;
  line-height: 20px;
  ${({ $home }) => ($home ? '' : 'color: var(--color-text-darker);')}

  ${shortScreen} {
    font-size: 13px;
    line-height: 18px;
  }
`;

const HomeNote = styled.span`
  font-size: 13px;
  line-height: 18px;
  opacity: 0.85;

  ${shortScreen} {
    font-size: 12px;
    line-height: 16px;
  }
`;

const PlayMark = styled.span`
  flex: none;
  box-sizing: border-box;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 2px solid currentColor;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 22px;
    height: 22px;
  }

  ${shortScreen} {
    width: 38px;
    height: 38px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const Note = styled.div`
  margin-top: 12px;
  font-size: 13px;
  line-height: 19px;
  color: var(--color-text-darker);

  ${shortScreen} {
    margin-top: 8px;
    font-size: 12px;
    line-height: 16px;
  }
`;

const Footnote = styled.div`
  margin-top: 8px;
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  color: var(--color-text-darker);

  ${shortScreen} {
    margin-top: 6px;
    font-size: 12px;
    line-height: 16px;
  }
`;
